import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  BarChart3,
  TrendingUp,
  Target,
  Clock,
  Calendar,
  Award,
  BookOpen,
  Brain,
  Zap,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  Download,
  Share2
} from 'lucide-react'
import { RootState } from '../store'
import { fetchStats } from '../store/slices/flashcardSlice'
import { FlashcardStats as StatsType } from '../types/flashcard'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import ProgressBar from '../components/ui/ProgressBar'

interface StatCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  color: string
  trend?: {
    value: number
    isPositive: boolean
  }
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  trend
}) => {
  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${color}`}>
              {icon}
            </div>
            <h3 className="font-medium text-gray-700">{title}</h3>
          </div>
          
          <div className="mb-1">
            <span className="text-3xl font-bold text-gray-900">{value}</span>
          </div>
          
          {subtitle && (
            <p className="text-sm text-gray-600">{subtitle}</p>
          )}
          
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${
              trend.isPositive ? 'text-green-600' : 'text-red-600'
            }`}>
              <TrendingUp 
                size={14} 
                className={trend.isPositive ? '' : 'rotate-180'} 
              />
              <span>{Math.abs(trend.value)}%</span>
              <span className="text-gray-500">
                {trend.isPositive ? 'artış' : 'azalış'}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

interface ProgressRingProps {
  progress: number
  size?: number
  strokeWidth?: number
  color?: string
}

const ProgressRing: React.FC<ProgressRingProps> = ({
  progress,
  size = 120,
  strokeWidth = 8,
  color = '#3B82F6'
}) => {
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const strokeDasharray = `${circumference} ${circumference}`
  const strokeDashoffset = circumference - (progress / 100) * circumference

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        className="transform -rotate-90"
        width={size}
        height={size}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-in-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-gray-900">
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  )
}

const FlashcardStats: React.FC = () => {
  const dispatch = useDispatch()
  const { stats, loading, error } = useSelector((state: RootState) => state.flashcard)
  const { loading: authLoading, isAuthenticated } = useSelector((state: RootState) => state.auth)
  
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('week')
  const [showFilters, setShowFilters] = useState(false)
  
  // Always fetch stats when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      dispatch(fetchStats() as any)
    }
  }, [isAuthenticated, authLoading, dispatch])

  // Additional effect to ensure stats are fetched on page refresh
  useEffect(() => {
    const shouldFetchStats = !authLoading && isAuthenticated
    if (shouldFetchStats) {
      // Small delay to ensure auth state is fully loaded
      const timer = setTimeout(() => {
        dispatch(fetchStats() as any)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [])
  
  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'week': return 'Bu Hafta'
      case 'month': return 'Bu Ay'
      case 'year': return 'Bu Yıl'
      default: return 'Bu Hafta'
    }
  }
  
  const getMasteryPercentage = () => {
    if (!stats || stats.cardsStudied === 0) return 0
    return (stats.cardsMastered / stats.cardsStudied) * 100
  }
  
  const getWeeklyProgress = () => {
    if (!stats) return 0
    return (stats.weeklyProgress / stats.weeklyGoal) * 100
  }
  
  const getStudyStreak = () => {
    return stats?.studyStreak || 0
  }
  
  const getAverageStudyTime = () => {
    if (!stats) return 0
    // Convert minutes to hours for display
    return Math.round(stats.totalStudyTime / 60 * 10) / 10
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  if (error || !stats) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {error || 'İstatistikler yüklenemedi'}
          </h2>
          <Button onClick={() => dispatch(fetchStats() as any)}>
            Tekrar Dene
          </Button>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Öğrenme İstatistikleri
              </h1>
              <p className="text-gray-600">
                Flashcard çalışma performansın ve ilerlemen
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-white rounded-lg border border-gray-200">
                {(['week', 'month', 'year'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      timeRange === range
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    } ${range === 'week' ? 'rounded-l-lg' : range === 'year' ? 'rounded-r-lg' : ''}`}
                  >
                    {range === 'week' ? 'Hafta' : range === 'month' ? 'Ay' : 'Yıl'}
                  </button>
                ))}
              </div>
              
              <Button variant="outline" size="sm">
                <Download size={16} className="mr-2" />
                Rapor İndir
              </Button>
              
              <Button variant="outline" size="sm">
                <Share2 size={16} className="mr-2" />
                Paylaş
              </Button>
            </div>
          </div>
        </div>
        
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Toplam Deste"
            value={stats.totalDecks}
            subtitle="Oluşturulan deste sayısı"
            icon={<BookOpen className="text-white" size={20} />}
            color="bg-blue-500"
          />
          
          <StatCard
            title="Toplam Kart"
            value={stats.totalCards}
            subtitle="Çalışılabilir kart sayısı"
            icon={<Target className="text-white" size={20} />}
            color="bg-green-500"
          />
          
          <StatCard
            title="Çalışma Süresi"
            value={`${getAverageStudyTime()}h`}
            subtitle="Toplam çalışma saati"
            icon={<Clock className="text-white" size={20} />}
            color="bg-purple-500"
          />
          
          <StatCard
            title="Çalışma Serisi"
            value={`${getStudyStreak()} gün`}
            subtitle="Kesintisiz çalışma"
            icon={<Zap className="text-white" size={20} />}
            color="bg-orange-500"
            trend={{
              value: 12,
              isPositive: true
            }}
          />
        </div>
        
        {/* Progress Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Weekly Goal Progress */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Haftalık Hedef</h3>
              <Badge className="bg-blue-100 text-blue-800">
                {getTimeRangeLabel()}
              </Badge>
            </div>
            
            <div className="flex items-center justify-center mb-4">
              <ProgressRing 
                progress={getWeeklyProgress()} 
                color="#3B82F6"
              />
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">
                {stats.weeklyProgress} / {stats.weeklyGoal} kart
              </p>
              <p className="text-xs text-gray-500">
                Hedefine {stats.weeklyGoal - stats.weeklyProgress} kart kaldı
              </p>
            </div>
          </Card>
          
          {/* Mastery Progress */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Öğrenme Oranı</h3>
              <Badge className="bg-green-100 text-green-800">
                Genel
              </Badge>
            </div>
            
            <div className="flex items-center justify-center mb-4">
              <ProgressRing 
                progress={getMasteryPercentage()} 
                color="#10B981"
              />
            </div>
            
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">
                {stats.cardsMastered} / {stats.cardsStudied} kart öğrenildi
              </p>
              <p className="text-xs text-gray-500">
                Ortalama başarı oranın: %{stats.averageScore}
              </p>
            </div>
          </Card>
          
          {/* Review Cards */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Tekrar Kartları</h3>
              <Badge className="bg-yellow-100 text-yellow-800">
                Bekliyor
              </Badge>
            </div>
            
            <div className="text-center mb-4">
              <div className="text-4xl font-bold text-yellow-600 mb-2">
                {stats.cardsToReview}
              </div>
              <p className="text-sm text-gray-600">
                Tekrar edilmesi gereken kart
              </p>
            </div>
            
            <Button className="w-full bg-yellow-600 hover:bg-yellow-700">
              <RefreshCw size={16} className="mr-2" />
              Tekrar Et
            </Button>
          </Card>
        </div>
        
        {/* Detailed Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Performance Breakdown */}
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 mb-6">Performans Dağılımı</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Öğrenilen Kartlar</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{stats.cardsMastered}</span>
                  <div className="w-24">
                    <ProgressBar 
                      progress={getMasteryPercentage()} 
                      className="h-2"
                      color="bg-green-500"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Tekrar Gerekli</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{stats.cardsToReview}</span>
                  <div className="w-24">
                    <ProgressBar 
                      progress={(stats.cardsToReview / stats.cardsStudied) * 100} 
                      className="h-2"
                      color="bg-yellow-500"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Yeni Kartlar</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {stats.totalCards - stats.cardsStudied}
                  </span>
                  <div className="w-24">
                    <ProgressBar 
                      progress={((stats.totalCards - stats.cardsStudied) / stats.totalCards) * 100} 
                      className="h-2"
                      color="bg-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          {/* Study Activity */}
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 mb-6">Çalışma Aktivitesi</h3>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-green-500" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Doğru Cevaplar</p>
                    <p className="text-sm text-gray-600">Bu hafta</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">156</p>
                  <p className="text-sm text-gray-500">+12%</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <XCircle className="text-red-500" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Yanlış Cevaplar</p>
                    <p className="text-sm text-gray-600">Bu hafta</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-red-600">24</p>
                  <p className="text-sm text-gray-500">-8%</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Brain className="text-purple-500" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Ortalama Süre</p>
                    <p className="text-sm text-gray-600">Kart başına</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-purple-600">45s</p>
                  <p className="text-sm text-gray-500">-5s</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Subject Performance */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Ders Bazında Performans</h3>
            <Button variant="outline" size="sm">
              <Filter size={16} className="mr-2" />
              Filtrele
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { subject: 'Matematik', progress: 85, cards: 45, color: 'bg-blue-500' },
              { subject: 'Türkçe', progress: 72, cards: 38, color: 'bg-green-500' },
              { subject: 'Fen Bilimleri', progress: 68, cards: 52, color: 'bg-purple-500' },
              { subject: 'Sosyal Bilgiler', progress: 91, cards: 29, color: 'bg-orange-500' },
              { subject: 'İngilizce', progress: 76, cards: 33, color: 'bg-red-500' },
              { subject: 'Din Kültürü', progress: 82, cards: 18, color: 'bg-teal-500' }
            ].map((item, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-4 h-4 rounded ${item.color}`}></div>
                  <h4 className="font-medium text-gray-900">{item.subject}</h4>
                </div>
                
                <div className="mb-2">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">İlerleme</span>
                    <span className="font-medium">{item.progress}%</span>
                  </div>
                  <ProgressBar progress={item.progress} className="h-2" />
                </div>
                
                <p className="text-sm text-gray-600">
                  {item.cards} kart çalışıldı
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  )
}

export default FlashcardStats